// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import { Body, Button, Card, H1, LgPadding, SmSpacing } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import {
  type FertilityCycleType,
  FertilityCycleTypeMap,
} from '@alto/scriptdash/alto/fertility_cycles/types/v1/fertility_cycle';
import React, { useState } from 'react';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { ProgynyPage } from '../../shared/components/ProgynyPage';
import { OrderEarlyFormFields } from './OrderEarlyFormFields';

export const OrderEarlyForm = () => {
  const { getParam, goBack, navigate } = useNavigation<'RouteOrderEarlyForm'>();
  const prescriptionId = getParam('prescriptionId');
  const { paymentMethods } = useGetPaymentMethods();
  const { trackEvent } = useAnalytics();

  // form inputs
  const [employer, setEmployer] = useState<string>('');
  const [cycleType, setCycleType] = useState<FertilityCycleType | undefined>(undefined);
  const [selectedBackupPaymentMethodId, setSelectedBackupPaymentMethodId] = useState<number | undefined>(undefined);

  // Autofill the most recently created payment method whenever it changes (adjust state during
  // render — no Effect needed). Compare by the most-recent id (a stable primitive) rather than the
  // `paymentMethods` array, because useGetPaymentMethods returns a fresh `[]` on every render while
  // loading — comparing the array reference would loop forever. `undefined` while empty means
  // loading renders don't fire, and a cached/loaded list autofills on its first render.
  const mostRecentPaymentMethodId =
    paymentMethods.length > 0 ? Math.max(...paymentMethods.map((pm) => pm.id)) : undefined;
  const [prevMostRecentPaymentMethodId, setPrevMostRecentPaymentMethodId] = useState<number | undefined>(undefined);
  if (mostRecentPaymentMethodId !== undefined && mostRecentPaymentMethodId !== prevMostRecentPaymentMethodId) {
    setPrevMostRecentPaymentMethodId(mostRecentPaymentMethodId);
    setSelectedBackupPaymentMethodId(mostRecentPaymentMethodId);
  }

  // input handlers
  const handleChangeEmployer = (value?: string) => {
    setEmployer(value || '');
  };

  const handleSelectCycleType = (value: string) => {
    const validCycleTypes = Object.values(FertilityCycleTypeMap) as readonly string[];
    const selectedCycleType = validCycleTypes.includes(value) ? (value as FertilityCycleType) : undefined;
    setCycleType(selectedCycleType);
  };

  const handleSelectPaymentMethod = (value: string) => {
    if (value === 'create_new') {
      // when the user selects "New Payment Method" from the dropdown,
      // we should navigate them to the payment method creation page
      // (which is named "SELECT_PAYMENT_METHOD" for some reason)
      trackEvent({ event: EVENTS.ADD_NEW_METHOD_TAPPED });
      setSelectedBackupPaymentMethodId(undefined);
      // Explicit undefined: navigate merges params, so a stale checkout context on an
      // already-mounted instance must be cleared - this flow picks a backup method in
      // local form state, not on the order.
      navigate('RouteSelectPaymentMethod', { paymentMethodType: undefined });
    } else {
      // otherwise, they have selected a payment method
      const id = parseInt(value, 10);
      setSelectedBackupPaymentMethodId(isNaN(id) ? undefined : id);
    }
  };

  // submission handler: checks for input errors and navigates to the next page
  const handlePressedNext = () => {
    navigate('RouteOrderEarlySubmit', {
      employer,
      cycleType,
      selectedBackupPaymentMethodId,
      prescriptionId,
    });
  };

  return (
    <ProgynyPage
      dismissIcon="chevronleft"
      onDismiss={() => {
        goBack();
      }}
      HeaderContent={
        <LgPadding topPadding={SPACING.STATIC.SM}>
          <H1 center>Order early</H1>
          <SmSpacing />
          <Body color={COLORS.TEXT_COLORS.GREY}>
            Request early ordering of your Progyny formulary medications. We will reach out for you to schedule in
            advance of your start date.
            {'\n\n'}
            Nothing will be charged at this time. After you order your medication, if your coverage is denied, then you
            will be responsible for the full cost of the drug.
          </Body>
        </LgPadding>
      }
      buttons={[
        <Button
          key="next"
          label="Next"
          onPress={handlePressedNext}
          disabled={!employer || !cycleType || !selectedBackupPaymentMethodId}
        />,
      ]}
    >
      <Card>
        <LgPadding>
          <OrderEarlyFormFields
            paymentMethods={paymentMethods}
            selectedBackupPaymentMethodId={selectedBackupPaymentMethodId}
            handleChangeEmployer={handleChangeEmployer}
            handleSelectCycleType={handleSelectCycleType}
            handleSelectPaymentMethod={handleSelectPaymentMethod}
          />
        </LgPadding>
      </Card>
    </ProgynyPage>
  );
};
